import { useTranslation } from 'react-i18next'

import UndoCheckingAttendance from '@/assets/icons/undo-checking-attendance.svg?react'
import Undo from '@/assets/icons/undo.svg?react'
import Alert from '@/components/Alert/Alert'
import Button from '@/components/Button/Button'
import ButtonIcon from '@/components/ButtonIcon/ButtonIcon'
import LinkText from '@/components/LinkText/LinkText'
import Radio from '@/components/Radio/Radio'
import Table, { type Column } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import Tooltip from '@/components/Tooltip/Tooltip'
import { useNumberColumn } from '@/hooks/useColumns'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import type { IdAndName } from '@/types/id-and-name'

import styles from './AttendanceTable.module.scss'
import type {
  Attendance,
  AttendanceCheckData,
  AttendancePresence
} from '../types'

type AttendanceTableProps = {
  attendances: Attendance[]
  isAttendanceCheckDisabled?: boolean
  isLoading?: boolean
  topBarAlert?: { text: string }
  onAttendanceChange: (attendance: AttendanceCheckData) => void
  onAttendanceClear: () => void
}

const AttendanceTable = (props: AttendanceTableProps) => {
  const { t } = useTranslation('lessonDetails')

  const { isMobile } = useScreenResolution()

  const attendanceOptions: {
    value: Exclude<AttendancePresence, null>
    label: string
  }[] = [
    { value: 'present', label: t('header.present') },
    { value: 'late', label: t('header.late') },
    { value: 'absent_unexcused', label: t('header.absent-unexcused') },
    { value: 'absent_excused', label: t('header.absent-excused') }
  ]

  const columns: Column<Attendance>[] = [
    useNumberColumn(),
    {
      dataIndex: 'student',
      key: 'student',
      title: t('header.student'),
      cellDataTestId: 'student-name',
      width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
      render: (value: IdAndName) => (
        <LinkText
          to="/students-and-classes/students/$studentId/details"
          params={{
            studentId: value.id
          }}
        >
          {value.name}
        </LinkText>
      )
    },
    ...attendanceOptions.map(option => ({
      width: TABLE_CELL_WIDTH.MD,
      key: option.value,
      render: (rowData: Attendance) => (
        <div className={styles.cell}>
          <Radio
            id={`radio-${option.value}-${rowData.id}`}
            name={`radio-${option.value}-${rowData.student.name}`
              .toLowerCase()
              .replaceAll(/[_ ]/g, '-')}
            value={option.value}
            label={`${rowData.student.name} ${option.label}`}
            hideLabel
            disabled={
              props.isAttendanceCheckDisabled ||
              option.value === 'absent_excused' ||
              rowData.student.status === 'archived'
            }
            checked={rowData.presence === option.value}
            onChange={value => {
              props.onAttendanceChange({
                id: rowData.id,
                presence: value || null
              })
            }}
          />
        </div>
      ),
      title: <span className={styles.cell}>{option.label}</span>
    })),
    {
      width: TABLE_CELL_WIDTH.ACTIONS,
      key: 'remove',
      className: 'actionCell',
      render: (value: Attendance) =>
        value.presence && !props.isAttendanceCheckDisabled ? (
          <Tooltip
            renderTriggerAsChild
            text={t('help.clear')}
            trigger={
              <ButtonIcon
                variant="tertiary"
                size="small"
                dataTestId={
                  'remove-single-attendance-' +
                  value.student.name.toLowerCase().replace(' ', '-')
                }
                onClick={() => {
                  props.onAttendanceChange({
                    id: value.id,
                    presence: null
                  })
                }}
                ariaLabel={t('button.clear')}
              >
                <Undo />
              </ButtonIcon>
            }
          />
        ) : null
    }
  ]

  const checkedAttendancesCount = props.attendances.filter(
    attendace => !!attendace.presence
  ).length

  return (
    <>
      <div className={styles.container}>
        <h2 className={styles.attendanceHeader}>{t('header.attendance')}</h2>

        <Table
          id="attendance-table"
          data={props.attendances}
          columns={columns}
          rightTopBar={
            <>
              {checkedAttendancesCount && !props.isAttendanceCheckDisabled ? (
                <Button
                  variant="tertiary"
                  dataTestId="clear-all-attendances"
                  size="small"
                  icon={<UndoCheckingAttendance />}
                  onClick={() => props.onAttendanceClear()}
                  ariaLabel={t('button.clear')}
                >
                  {t('button.clear-all')}
                </Button>
              ) : null}
              {props.topBarAlert ? (
                <Alert
                  message={
                    <span className={styles.alertText}>
                      {props.topBarAlert.text}
                    </span>
                  }
                  variant="warning"
                  withoutBackground
                />
              ) : null}
            </>
          }
          leftTopBar={
            <div className={styles.headerTable}>
              <div className={styles.selectionWrapper}>
                <span className={styles.topBarText}>
                  {t('text.checked-text', {
                    CHECKED_COUNT: checkedAttendancesCount,
                    ALL_COUNT: props.attendances.length
                  })}
                </span>
              </div>
            </div>
          }
          caption={t('header.attendance')}
        />
      </div>
    </>
  )
}

export default AttendanceTable
